/**
 * École Nid Douillet - Public Homepage
 * 
 * Dynamic CMS-powered homepage for the school website
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cmsApi, CMSContent } from '@/services/cms.api';
import { 
  AcademicCapIcon, 
  UserGroupIcon, 
  ShieldCheckIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

const HomePage: React.FC = () => {
  const [homeContent, setHomeContent] = useState<CMSContent | null>(null);
  const [newsItems, setNewsItems] = useState<CMSContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load homepage content and recent news
        const [homeResponse, newsResponse] = await Promise.all([
          cmsApi.getHomePage('fr'),
          cmsApi.getPublishedNews('fr')
        ]);

        if (homeResponse.success && homeResponse.data) {
          setHomeContent(homeResponse.data);
        }

        if (newsResponse.success && newsResponse.data) {
          setNewsItems(newsResponse.data.slice(0, 3)); // Show latest 3 news items
        }

      } catch (err) {
        console.error('Error loading homepage content:', err);
        setError('Erreur lors du chargement du contenu');
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  const renderContent = (content: string) => {
    return { __html: content };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              École Nid Douillet
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              {homeContent?.excerpt || 'École maternelle bilingue français-arabe située à Tilila, Agadir. Un environnement chaleureux et bienveillant pour l\'épanouissement de votre enfant.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/a-propos" 
                className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-neutral-100 transition-colors inline-block"
              >
                Découvrir l'école
              </Link>
              <Link 
                to="/contact" 
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors inline-block"
              >
                Nous contacter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6 text-neutral-900">
              Pourquoi choisir l'École Nid Douillet ?
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Une éducation de qualité dans un environnement bienveillant et multiculturel
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AcademicCapIcon className="h-10 w-10 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-neutral-900">Éducation Bilingue</h3>
              <p className="text-neutral-600 leading-relaxed">
                Apprentissage équilibré du français et de l'arabe dès le plus jeune âge, 
                préparant nos enfants à un avenir multilingue.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <UserGroupIcon className="h-10 w-10 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-neutral-900">Équipe Qualifiée</h3>
              <p className="text-neutral-600 leading-relaxed">
                Enseignants expérimentés et passionnés par l'éducation de la petite enfance, 
                formés aux méthodes pédagogiques modernes.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShieldCheckIcon className="h-10 w-10 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-neutral-900">Environnement Sécurisé</h3>
              <p className="text-neutral-600 leading-relaxed">
                Locaux modernes et sécurisés adaptés aux besoins des jeunes enfants, 
                avec des espaces d'apprentissage stimulants.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Content Section */}
      {homeContent && (
        <section className="py-16 lg:py-24 bg-neutral-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={renderContent(homeContent.content)}
              />
            </div>
          </div>
        </section>
      )}

      {/* News Section */}
      {newsItems.length > 0 && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6 text-neutral-900">
                Actualités
              </h2>
              <p className="text-xl text-neutral-600">
                Restez informés des dernières nouvelles de l'école
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {newsItems.map((news) => (
                <article key={news.id} className="bg-white rounded-xl shadow-soft overflow-hidden hover:shadow-lg transition-shadow">
                  {news.featured_image && (
                    <img 
                      src={news.featured_image} 
                      alt={news.title}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-3 text-neutral-900">
                      {news.title}
                    </h3>
                    <p className="text-neutral-600 mb-4 line-clamp-3">
                      {news.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">
                        {news.published_at && new Date(news.published_at).toLocaleDateString('fr-FR')}
                      </span>
                      <Link 
                        to={`/actualites/${news.slug}`}
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Lire plus →
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <Link 
                to="/actualites"
                className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors inline-block"
              >
                Voir toutes les actualités
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA Section */}
      <section className="py-16 lg:py-24 bg-primary-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Prêt à inscrire votre enfant ?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Contactez-nous dès aujourd'hui pour planifier une visite et découvrir 
              tout ce que l'École Nid Douillet peut offrir à votre enfant.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="flex items-center justify-center space-x-3">
                <PhoneIcon className="h-6 w-6" />
                <span>+212 668 78 63 68</span>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <EnvelopeIcon className="h-6 w-6" />
                <span>contact@ecoleniddouillet.com</span>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <MapPinIcon className="h-6 w-6" />
                <span>Tilila, Agadir</span>
              </div>
            </div>
            
            <Link 
              to="/contact"
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-neutral-100 transition-colors inline-block"
            >
              Nous contacter
            </Link>
          </div>
        </div>
      </section>

      {/* Error Display */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white p-4 rounded-lg shadow-lg">
          {error}
        </div>
      )}
    </div>
  );
};

export default HomePage;
